import type { ReactElement, ReactNode } from "react";

import { Heading } from "@/components/ui/heading";
import { cn } from "@/lib/utils";

export type PageHeaderProps = {
  title: ReactNode;
  /** Muted supporting line under the title (e.g. settings description). */
  description?: ReactNode;
  /** Primary actions aligned to the end of the row on larger viewports. */
  actions?: ReactNode;
  /** Rendered above the title row (e.g. back link). */
  back?: ReactNode;
  className?: string;
};

export function PageHeader(props: PageHeaderProps): ReactElement {
  const { title, description, actions, back, className } = props;

  const titleStack = (
    <div className="flex min-w-0 flex-col gap-1">
      <Heading variant="page">{title}</Heading>
      {description != null ? <p className="text-sm text-muted-foreground">{description}</p> : null}
    </div>
  );

  const headerBody =
    actions != null ? (
      <header
        className={cn("flex flex-wrap items-center justify-between gap-4", className)}
        data-slot="page-header"
      >
        {titleStack}
        <div className="flex w-full shrink-0 justify-end sm:w-auto">{actions}</div>
      </header>
    ) : (
      <header className={cn("flex flex-col", className)} data-slot="page-header">
        {titleStack}
      </header>
    );

  if (back != null) {
    return (
      <div className="flex flex-col gap-4">
        {back}
        {headerBody}
      </div>
    );
  }

  return headerBody;
}
